/**
 * Close register · Z-report (S42) — sales by tender + drawer count → variance.
 * @see prototype/pos/shift-close.html
 */
import { useState } from "react";
import { toast } from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import { formatRupees } from "../invoices/lib/format-money.ts";

const toPaise = (s: string) => BigInt(Math.round((parseFloat(s.replace(/[^0-9.]/g, "")) || 0) * 100));

export function CloseRegisterPage({
  shiftId,
  onClosed,
  onCancel,
}: {
  shiftId: string;
  onClosed: () => void;
  onCancel: () => void;
}) {
  const sumQ = trpc.pos.shiftSummary.useQuery({ shiftId });
  const [counted, setCounted] = useState("");
  const [reason, setReason] = useState("");
  const close = trpc.pos.closeShift.useMutation({
    onSuccess: (d) => {
      toast.success(`Register closed — variance ${formatRupees(d.variancePaise)}`);
      onClosed();
    },
    onError: (e) => toast.error("Couldn't close the register", { description: e.message }),
  });

  const s = sumQ.data;
  const expected = BigInt(s?.expectedCashPaise ?? "0");
  const countedPaise = counted ? toPaise(counted) : 0n;
  const variance = countedPaise - expected;
  const balanced = counted !== "" && variance === 0n;
  const needsReason = counted !== "" && variance !== 0n;

  return (
    <div className="flex min-h-screen flex-col bg-bg text-ink">
      <header className="flex h-14 flex-shrink-0 items-center gap-3 border-b border-line bg-paper px-4">
        <div className="grid h-8 w-8 place-items-center rounded-md bg-ink text-sm font-extrabold text-white">F</div>
        <div className="text-sm font-bold">Flint POS</div>
        <div className="flex-1" />
        <button onClick={onCancel} className="grid h-9 w-9 place-items-center rounded-md border border-line bg-paper text-ink-2 hover:bg-bg" aria-label="Back">←</button>
      </header>

      <main className="mx-auto w-full max-w-3xl px-4 pb-28 pt-6">
        <div className="text-xs font-bold uppercase tracking-[0.12em] text-sky-700">End of day</div>
        <h1 className="mt-1.5 text-2xl font-bold tracking-tight">Close register · Z-report</h1>
        <p className="mt-1 text-sm text-muted">Count the drawer to reconcile against expected cash.</p>

        {sumQ.isLoading ? (
          <div className="mt-6 text-sm text-muted">Loading shift…</div>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Sales by tender + drawer movements */}
            <div className="space-y-4">
              <div className="rounded-xl border border-line bg-paper shadow-sm">
                <div className="border-b border-line px-4 py-3 text-sm font-semibold">
                  Sales by tender · {s?.salesCount ?? 0} sales
                </div>
                <div className="px-4">
                  <Row k="Cash" v={formatRupees(s?.cashPaise)} />
                  <Row k="Card" v={formatRupees(s?.cardPaise)} />
                  <Row k="UPI" v={formatRupees(s?.upiPaise)} />
                  <Row k="Total collected" v={formatRupees(s?.grossSalesPaise)} bold top />
                </div>
              </div>
              <div className="rounded-xl border border-line bg-paper shadow-sm">
                <div className="border-b border-line px-4 py-3 text-sm font-semibold">Cash drawer</div>
                <div className="px-4">
                  <Row k="Opening float" v={`+${formatRupees(s?.openingFloatPaise)}`} />
                  <Row k="Cash sales" v={`+${formatRupees(s?.cashPaise)}`} />
                  <Row k="Expected in drawer" v={formatRupees(s?.expectedCashPaise)} bold top />
                </div>
              </div>
            </div>

            {/* Count + variance */}
            <div className="rounded-xl border border-line bg-paper p-5 shadow-sm">
              <div className="text-sm font-semibold">Count the drawer</div>
              <div className="mt-3 flex items-center justify-between rounded-lg border border-line bg-raised px-3.5 py-3">
                <div className="text-xs font-semibold text-muted">
                  Expected cash<br /><span className="font-normal">float + cash sales</span>
                </div>
                <div className="font-mono text-xl font-bold">{formatRupees(s?.expectedCashPaise)}</div>
              </div>
              <div className="mt-3.5">
                <div className="mb-1.5 text-xs font-semibold text-ink-2">Counted cash</div>
                <div className="flex h-16 items-center gap-2 rounded-lg border-[1.5px] border-line-2 bg-paper px-4 focus-within:border-sky-600 focus-within:ring-4 focus-within:ring-sky-100">
                  <span className="text-2xl font-bold text-muted">₹</span>
                  <input
                    value={counted}
                    onChange={(e) => setCounted(e.target.value)}
                    inputMode="decimal"
                    autoFocus
                    placeholder="0"
                    className="w-full border-0 bg-transparent font-mono text-3xl font-bold tracking-tight text-ink outline-none placeholder:text-muted"
                  />
                </div>
              </div>
              {counted !== "" && (
                <div
                  className={`mt-3.5 flex items-center justify-between rounded-lg border px-4 py-3.5 ${
                    balanced ? "border-ok-100 bg-ok-50" : variance < 0n ? "border-bad-100 bg-bad-50" : "border-warn-100 bg-warn-50"
                  }`}
                >
                  <div>
                    <div className="text-sm font-bold">
                      {balanced ? "Drawer balanced" : variance < 0n ? `Short by ${formatRupees((-variance).toString())}` : `Over by ${formatRupees(variance.toString())}`}
                    </div>
                    <div className="text-xs text-muted">{balanced ? "Counted matches expected" : "Counted vs expected"}</div>
                  </div>
                  <div className={`font-mono text-xl font-bold ${balanced ? "text-ok" : variance < 0n ? "text-bad" : "text-warn"}`}>
                    {variance === 0n ? "₹0.00" : `${variance < 0n ? "−" : "+"}${formatRupees((variance < 0n ? -variance : variance).toString())}`}
                  </div>
                </div>
              )}
              {needsReason && (
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason for variance (required) — e.g. ₹60 short, change error on a cash sale."
                  className="mt-3 min-h-[64px] w-full resize-y rounded-md border border-line bg-paper p-2.5 text-sm text-ink outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100"
                />
              )}
            </div>
          </div>
        )}
      </main>

      <div className="fixed inset-x-0 bottom-0 border-t border-line bg-paper px-4 py-3.5">
        <div className="mx-auto flex max-w-3xl justify-end">
          <button
            disabled={counted === "" || close.isPending || (needsReason && reason.trim() === "")}
            onClick={() =>
              close.mutate({
                shiftId,
                countedCashPaise: String(countedPaise),
                ...(reason.trim() ? { varianceReason: reason.trim() } : {}),
              })
            }
            className="flex h-14 items-center justify-center gap-2 rounded-lg bg-ink px-8 text-base font-bold text-white shadow-md hover:bg-ink-2 disabled:opacity-50"
          >
            {close.isPending ? "Closing…" : "Close register"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, bold, top }: { k: string; v: string; bold?: boolean; top?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-3 ${top ? "border-t-2 border-line" : "border-b border-line last:border-b-0"}`}>
      <span className={`text-sm ${bold ? "font-bold text-ink" : "text-ink-2"}`}>{k}</span>
      <span className={`font-mono ${bold ? "text-lg font-bold" : "text-sm font-semibold"}`}>{v}</span>
    </div>
  );
}
